import { APP_BASE_HREF, CommonModule } from '@angular/common'
import { Component, Signal, computed, effect, inject, input } from '@angular/core'
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms'
import { Router } from '@angular/router'

import { DividerModule } from 'primeng/divider'

import { FlxButtonModule, FlxCardModule, FlxIconModule } from '@flowx/angular-ui-toolkit'
import { FlxBuildTagComponent } from '@flowx/designer/modules/projects/components/build-tag/build-tag.component'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { CodeEditorComponent } from '@flowx/designer/modules/script-builder/components/code-editor/code-editor.component'
import {
  ExceptionTypeEnum,
  ProcessException,
} from '@flowx/designer/shared/entities/process-exceptions.types'
import { ApplicationDetails } from '@flowx/designer/shared/entities/process-instance.types'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'
import { BranchNameComponent } from '@flowx/designer/shared/versioning/branch-name/branch-name.component'
import { BranchStateIconComponent } from '@flowx/designer/shared/versioning/branch-state-icon/branch-state-icon.component'
import { FlxBranchStateComponent } from '@flowx/designer/shared/versioning/branch-state/branch-state.component'

@Component({
  selector: 'flx-exception-details-content',
  imports: [
    CommonModule,
    BranchNameComponent,
    BranchStateIconComponent,
    CodeEditorComponent,
    DividerModule,
    FlxBranchStateComponent,
    FlxBuildTagComponent,
    FlxIconModule,
    ReactiveFormsModule,
    FlxCardModule,
    FlxButtonModule,
  ],
  templateUrl: './exception-details-content.component.html',
  host: { class: 'h-full' },
})
export class ExceptionDetailsContentComponent {
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #urlParamsService = inject(UrlParametersStoreService)
  #router = inject(Router)
  #baseHref = inject(APP_BASE_HREF)

  readonly editorOptions = { language: 'javascript', wordWrap: 'on' }
  definitionsExist: Signal<boolean> = computed(() => {
    const editAppVersion = this.#applicationDetailsStore.applicationDefinition()?.editAppVersion
    const applicationVersion = this.#applicationDetailsStore.applicationVersion()
    return editAppVersion !== null || applicationVersion !== null
  })
  appName = this.#applicationDetailsStore.appName
  processExceptionDetailsFC = new UntypedFormControl(null)
  processException = input.required<ProcessException>()
  applicationDetails = input.required<ApplicationDetails>()
  loading = input.required<boolean>()
  detailFields = computed(() => {
    const processException = this.processException()
    const applicationDetails = this.applicationDetails()

    const allPossibleFields = [
      {
        field: 'processDefinitionName',
        secondaryField: 'processDefinitionFlowxUuid',
        column: 'left',
      },
      {
        header: 'Build:',
        field: 'build',
        condition: !applicationDetails?.buildWip && applicationDetails?.buildTag,
        column: 'left',
      },
      {
        header: 'Application:',
        field: 'application',
        condition: !(!applicationDetails?.buildWip && applicationDetails?.buildTag),
        column: 'left',
      },
      {
        header: 'Build created from:',
        field: 'buildCreatedFrom',
        condition: !(!applicationDetails?.buildWip && applicationDetails?.buildTag),
        column: 'left',
      },
      {
        header: 'Application version ID:',
        field: 'applicationVersionId',
        condition: !(!applicationDetails?.buildWip && applicationDetails?.buildTag),
        column: 'left',
      },
      {
        header: 'Process Instance UUID:',
        field: 'processInstanceUuid',
        condition: processException?.type !== ExceptionTypeEnum.DEFINITION_EXCEPTION,
        column: 'left',
      },
      {
        header: 'Token UUID:',
        field: 'tokenInstanceUuid',
        condition: processException?.type !== ExceptionTypeEnum.DEFINITION_EXCEPTION,
        column: 'left',
      },
      {
        header: 'Source:',
        field: 'formattedSource',
        column: 'right',
      },
      {
        header: 'Cause Type:',
        field: 'causeType',
        column: 'right',
      },
      {
        header: 'Type:',
        field: 'formattedType',
        condition: processException?.type !== ExceptionTypeEnum.DEFINITION_EXCEPTION,
        column: 'right',
      },
      {
        header: 'Timestamp:',
        field: 'date',
        column: 'right',
      },
      {
        header: 'Message:',
        field: 'message',
        column: 'right',
      },
    ]

    const filteredFields = allPossibleFields
      .filter((field) => field.condition ?? true)
      .map(({ condition, ...field }) => field)

    const leftColumnFields = filteredFields.filter((field) => field.column === 'left')
    const rightColumnFields = filteredFields.filter((field) => field.column === 'right')

    return {
      leftColumnFields,
      rightColumnFields,
    }
  })

  constructor() {
    effect(() => {
      const processException = this.processException()
      this.processExceptionDetailsFC.setValue(processException?.details)
    })
  }

  goToDefinition(processException: ProcessException, applicationDetails: ApplicationDetails): void {
    const { appId, workspaceId } = this.#urlParamsService.urlParameters
    const appVersionId = applicationDetails?.buildId
    if (appId && appVersionId) {
      const link = this.#router.serializeUrl(
        this.#router.createUrlTree(
          [
            this.#baseHref,
            'guard',
            'workspace',
            workspaceId,
            'projects',
            appId,
            'config',
            appVersionId,
            'processes',
            processException.resourceId,
            'diagram',
          ],
          {}
        )
      )
      window.open(link, '_blank')
    }
  }
}
